/**
 * Тост макета: `.toast` фиксирован снизу по центру, показывается классом `.show`,
 * автоскрытие через 3200 мс, предыдущий таймер сбрасывается.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

export const TOAST_TIMEOUT_MS = 3200;

export type ShowToast = (message: string) => void;

const ToastContext = createContext<ShowToast | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [message, setMessage] = useState('');
    const [visible, setVisible] = useState(false);
    const timer = useRef<number | undefined>(undefined);

    const show = useCallback<ShowToast>((text) => {
        setMessage(text);
        setVisible(true);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setVisible(false), TOAST_TIMEOUT_MS);
    }, []);

    useEffect(
        () => () => {
            if (timer.current) window.clearTimeout(timer.current);
        },
        [],
    );

    return (
        <ToastContext.Provider value={show}>
            {children}
            {createPortal(
                <div className={visible ? 'toast show' : 'toast'} role="status" aria-live="polite">
                    {message}
                </div>,
                document.body,
            )}
        </ToastContext.Provider>
    );
}

/** `const toast = useToast(); toast('Ответ согласован');` */
export function useToast(): ShowToast {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast вызван вне ToastProvider');
    return ctx;
}
